/**
 * Centralised permission-key builders, mirroring incept-api's
 * Incept\Access\Permission generator classes (ScopedPermission,
 * DocumentPermission, FormPermission, SummaryPermission, ModulePermission,
 * IntegrationPermission) so every composed key matches the shapes the
 * backend seeds and the trie matcher expects.
 *
 * NEVER assemble permission strings ad hoc (template literals scattered in
 * config); compose them here so wildcard placement and segment order stay
 * consistent with the backend. Keys that exist statically should come from
 * the generated vocabulary modules instead; these builders are for keys
 * derived from runtime values (document definition names, form identifiers).
 */

export const PERMISSION_WILDCARD = '*';

export type PermissionAction =
  | 'view'
  | 'create'
  | 'update'
  | 'delete'
  | 'restore'
  | 'download'
  | typeof PERMISSION_WILDCARD;

const joinKey = (...segments: string[]): string => segments.join('.');

/** document.{type}.sets.{set}.{action} */
export const buildDocumentPermission = (
  type: string,
  set: string,
  action: PermissionAction,
): string => joinKey('document', type, 'sets', set, action);

/** document.{type}.fields.{field}.{action} */
export const buildDocumentFieldPermission = (
  type: string,
  field: string,
  action: PermissionAction,
): string => joinKey('document', type, 'fields', field, action);

/** forms.{form}.{action} */
export const buildFormPermission = (
  form: string,
  action: PermissionAction,
): string => joinKey('forms', form, action);

/** forms.{form}.actions.{customAction} */
export const buildFormActionPermission = (
  form: string,
  customAction: string,
): string => joinKey('forms', form, 'actions', customAction);

/** summary.{definition}.template.{template}.block.{block}.{action} */
export const buildSummaryPermission = (
  definition: string,
  template: string,
  block: string,
  action: PermissionAction,
): string =>
  joinKey('summary', definition, 'template', template, 'block', block, action);

/** module.{identifier}.access (hyphenated identifiers are snake_cased, matching ModulePermission) */
export const buildModulePermission = (moduleIdentifier: string): string =>
  joinKey('module', moduleIdentifier.replace(/-/g, '_'), 'access');

/** integration.{source}.{integration}.run */
export const buildIntegrationPermission = (
  source: string,
  integration: string,
): string => joinKey('integration', source, integration, 'run');

/**
 * Guard for BUILT keys: returns a validator that throws when a composed key
 * is not part of the given generated vocabulary, so a bad runtime derivation
 * fails fast instead of producing a string no grant can ever match. Pass the
 * calling side's own generated Permissions module; core must never import a
 * tenant vocabulary itself.
 */
export const createVocabularyValidator = (
  vocabulary: Record<string, string>,
): ((key: string) => string) => {
  const known = new Set<string>(Object.values(vocabulary));

  return (key: string): string => {
    if (!known.has(key)) {
      throw new Error(
        `Unknown permission "${key}"; fix the derivation or regenerate the vocabulary from incept-api.`,
      );
    }
    return key;
  };
};
